<template>
	<Modals
		class="customer__modal"
		:title="'Beta Features'"
		:display="display"
		@show="close"
		:parentTag="'form'"
		:novalidate="'novalidate'"
		:fullMobile="true"
	>
		<template #modal-content>
			<div class="customer__content">
				<div class="customer__name">{{ customerName }}</div>
				<Divider class="customer__divider" />
				<div class="customer__beta-features" ref="customerFeature">
					<div class="customer__beta-features-header">
						All Beta Features Applied:
					</div>
					<div class="customer__beta-features-data-text">
						{{ renderFeatureDescription }}

						<Button
							v-if="showMoreLessButton"
							className="btn--inline-link"
							class="customer__beta-features-data-btn"
							:labelText="
								!betaFeatureMoreLessTextButton ? 'More' : 'Less'
							"
							:btnClick="showMore"
						/>
					</div>
				</div>
				<ul
					class="companies__list"
					v-if="
						!hasClientCompaniesandLocationsSameBetaFeatures &&
							companies.length > 0
					"
				>
					<li
						v-for="(company, index) in companies"
						:key="index"
						class="companies__list-item"
					>
						<Divider class="customer__divider" />
						<CompanyLocationFeatureCard
							:number="company.companyNumber"
							:companyLocation="company"
							:iconClass="'icon icon-company'"
							class="companies__company-locations"
						>
							<template #ellipsis>
								<Ellipsis
									:data="company.companyName"
									class="companies__company-locations-name"
								/>
							</template>
						</CompanyLocationFeatureCard>
						<ul
							v-if="
								company.locations &&
									company.locations.length > 0
							"
							class="companies__list-locations"
						>
							<li
								v-for="(location, index) in company.locations"
								:key="index"
							>
								<CompanyLocationFeatureCard
									:number="location.locationNumber"
									:companyLocation="location"
									:iconClass="'icon icon-location'"
									v-if="location.activeBetaFeature"
									class="companies__company-locations"
								>
									<template #ellipsis>
										<Ellipsis
											:data="location.locationName"
											class="companies__company-locations-name"
										/>
									</template>
								</CompanyLocationFeatureCard>
							</li>
						</ul>
					</li>
				</ul>
				<div
					class="customer__same-features"
					ref="customerFeature"
					v-if="
						hasClientCompaniesandLocationsSameBetaFeatures &&
							companies.length > 0
					"
				>
					<Divider class="customer__divider" />
					<div class="customer__same-features-data-text">
						Same Beta Features applied for Client, Companies and
						Locations
					</div>
				</div>
			</div>
		</template>
	</Modals>
</template>

<script>
import Button from '@/components/forms/button/button';
import Divider from '@/components/shells/divider/divider';
import CompanyLocationFeatureCard from '@/sections/customers/features/company-location-features-card';
import Modals from '@/components/shells/modal/modals';
import Ellipsis from '@/components/visual/ellipsis';
import _ from 'lodash';
// vue
import { mapState } from 'vuex';

export default {
	name: 'CustomerFeaturesModal',
	components: {
		Button,
		Divider,
		CompanyLocationFeatureCard,
		Modals,
		Ellipsis
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		display: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			fullFeatureDescription: '',
			featureDescription: '',
			showMoreOrLessButton: false,
			betaFeatureMoreLessTextButton: false,
			fixedHeight: { height: 100, unit: '%' }
		};
	},
	computed: {
		...mapState('customer', ['customerFeatures']),

		customerName() {
			return _.get(this.customerFeatures, 'customerName', '');
		},
		companies() {
			return _.get(this.customerFeatures, 'companies', []);
		},
		showMoreLessButton() {
			return this.showMoreOrLessButton;
		},
		renderFeatureDescription() {
			if (this.fullFeatureDescription) {
				if (
					(this.showMoreOrLessButton ||
						_.get(this, 'this.featureDescription', '') != '') &&
					!this.betaFeatureMoreLessTextButton
				) {
					return `${this.featureDescription}...`;
				} else {
					return this.fullFeatureDescription;
				}
			}
			return '';
		},
		hasClientCompaniesandLocationsSameBetaFeatures() {
			const customerFeatures = _.get(
				this.customerFeatures,
				'features',
				[]
			).map(feature => feature.id);

			const companies = _.get(this.customerFeatures, 'companies', []);

			for (const company of companies) {
				const companyFeatures = _.get(company, 'features', []).map(
					feature => feature.id
				);
				if (
					!_.isEqual(customerFeatures.sort(), companyFeatures.sort())
				) {
					return false;
				}

				const locations = _.get(company, 'locations', []);
				for (const location of locations) {
					const locationFeatures = _.get(
						location,
						'features',
						[]
					).map(feature => feature.id);
					if (
						!_.isEqual(
							customerFeatures.sort(),
							locationFeatures.sort()
						)
					) {
						return false;
					}
				}
			}

			return true;
		}
	},
	methods: {
		showMore(e) {
			if (e) {
				e.preventDefault();
			}
			this.betaFeatureMoreLessTextButton = !this
				.betaFeatureMoreLessTextButton;
		},
		close() {
			this.$store.commit('customer/SetCustomerFeatures', {});
		}
	},
	watch: {
		customerFeatures: {
			deep: true,
			immediate: true,
			handler(obj) {
				if (!_.isEmpty(obj)) {
					this.$nextTick(() => {
						this.showMoreOrLessButton = false;
						if (this.$refs.customerFeature) {
							const PIXEL_TO_CHARACTER = 0.125;
							const clientWidth = this.$refs.customerFeature
								.clientWidth;

							const pixelToCharacterLen =
								clientWidth * PIXEL_TO_CHARACTER;

							if (obj.features.length) {
								const { features } = obj;
								const fullDescription = features
									.map(feature => feature.description)
									.join(', ');

								if (
									fullDescription.length > pixelToCharacterLen
								) {
									this.showMoreOrLessButton = true;
									const INITIAL_POSITION = 0;
									const FINAL_POSITION = pixelToCharacterLen;

									this.featureDescription = fullDescription.slice(
										INITIAL_POSITION,
										FINAL_POSITION
									);
								}
								this.fullFeatureDescription = fullDescription;
							}
						}
					});
				}
			}
		},
		display: {
			immediate: true,
			handler(val) {
				if (val) {
					this.betaFeatureMoreLessTextButton = false;
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.customer {
	&__content {
		width: 600px;
	}
	&__name {
		font-family: $secondary-font-family;
		font-size: $heading-font-size-lg;
	}
	&__divider {
		width: 100%;
	}
	&__beta-features {
		display: flex;
		flex-direction: column;
		margin: $gutter-lg 0;

		&-header {
			font-size: $heading-font-size-md;
			font-weight: $heading-font-weight-md;
		}
		&-data-text {
			font-size: $bd-txt-font-size-regular;
		}
	}
	&__same-features {
		display: flex;
		flex-direction: column;
		gap: $gutter-lg;

		&-data-text {
			font-size: $bd-txt-font-size-regular;
		}
	}
}

.companies {
	&__name {
		@include heading(md);
	}
	&__list {
		display: flex;
		flex-direction: column;
		gap: $gutter-lg;

		&-item {
			display: flex;
			flex-direction: column;
			gap: $gutter-lg;
		}
		&-locations {
			margin-left: $gutter-xl;
			display: flex;
			flex-direction: column;
			gap: $gutter-md;
		}
	}
	&__company-locations {
		&-name {
			font-size: $bd-txt-font-size-regular;
			font-weight: $medium-font-weight;
		}
	}
}
</style>
